import React, { useState } from "react";
import { TextInputForm } from "./text_input";
import { Constants } from "../../../../common/constants";
import FadeIn from "../../../../src/vendored/FadeIn";
import { PollOption, PollType } from "../../../../common/sesh_types";
import {
	AddonButtonProps,
	DashboardUtils,
	FADE_IN_ANIMATION_DURATION,
} from "../dashboard_utils";
import { DateTimePicker } from "./date_time_picker";
import { cloneDeep } from "lodash";

interface PollOptionsProps {
	name?: string;
	initial_value: PollOption[];
	updateFunc: (options: PollOption[], type: PollType) => void;
	do_not_update_initially?: boolean;
	disabled?: boolean;
	info?: string;
	guild_timezone: string;
	personal_timezone: string;
	use_24h_clock: boolean;
	is_edit?: boolean;
	poll_type: PollType;
}

interface InputFormState {
	options: PollOption[];
	cleaned_options: PollOption[];
	poll_type: PollType;
}

function getListWithoutNulls(list: Array<any>) {
	return list.filter((val) => val != null);
}

export function createInitialPollOption(type: PollType): PollOption {
	let name: string;
	switch (type) {
		case PollType.Standard:
			name = "";
			break;
		case PollType.Time:
			const date = new Date();

			const nearest_15_minute = Math.ceil(date.getMinutes() / 15) * 15;
			date.setMinutes(nearest_15_minute);
			date.setMilliseconds(0);
			date.setSeconds(0);

			name = date.toISOString();
			break;
	}

	return {
		name: name,
		is_newly_added: true,
	};
}

export function parseTimePollOption(poll_option_string: string) {
	const option_words = poll_option_string.split(" ");
	const date = new Date(option_words[0]);
	let creator_string: string;
	if (option_words.length > 1) {
		creator_string = option_words.slice(1).join(" ");
	}
	return { date, creator_string };
}

export class PollOptionsList extends React.Component<
	PollOptionsProps,
	InputFormState
> {
	constructor(props) {
		super(props);

		let value = this.props.initial_value
			? this.props.initial_value
			: [createInitialPollOption(this.props.poll_type)];

		this.state = {
			options: value,
			cleaned_options: value,
			poll_type: this.props.poll_type,
		};

		this.handleChange = this.handleChange.bind(this);
		this.onDelete = this.onDelete.bind(this);
		this.onAddButtonPressed = this.onAddButtonPressed.bind(this);
		this.updateParent = this.updateParent.bind(this);
	}

	onDelete(index) {
		this.state.options[index] = null;
		this.updateParent(this.state.options, this.state.poll_type);
	}

	createPollOption(index: number, option: PollOption) {
		const key = index + "_child";

		switch (this.state.poll_type) {
			case PollType.Standard:
				return (
					<div className="column is-11">
						<TextInputForm
							key={key}
							initial_value={option.name}
							char_limit={
								Constants.POLL_OPTION_CHARACTER_LIMIT - 1
							}
							updateFunc={(val) => {
								let value = {
									name: val,
									is_newly_added: true,
								};
								this.handleChange(index, value);
							}}
							placeholder={"Poll option"}
							disabled={
								!option.is_newly_added || this.props.disabled
							}
						></TextInputForm>
					</div>
				);
			case PollType.Time:
				return (
					<div className="column is-8-tablet is-11-mobile">
						<DateTimePicker
							key={key}
							initial_value={
								option?.name
									? parseTimePollOption(option.name).date
									: undefined
							}
							updateFunc={(val) => {
								this.handleChange(index, {
									name: val.toISOString(),
									is_newly_added: true,
								});
							}}
							disabled={
								!option.is_newly_added || this.props.disabled
							}
							disable_past_days={false}
						/>
					</div>
				);
		}
	}

	PollOption(index, deletable) {
		let option = this.state.options[index];
		let key = index;

		return (
			<div key={key + "_parent"} className="columns is-mobile">
				<div className="column !py-1">
					<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
						<div className="columns is-gapless is-vcentered is-mobile">
							{this.createPollOption(index, option)}
							{this.state.cleaned_options.length > 1 &&
								deletable && (
									<div
										className="column is-1 is-centered has-text-centered is-vcentered"
										onClick={() => this.onDelete(index)}
									>
										<DashboardUtils.DeleteButton
											onClick={() => this.onDelete(index)}
										/>
									</div>
								)}
						</div>
					</FadeIn>
				</div>
			</div>
		);
	}

	handleChange(
		index: number,
		option_value: { name: string; is_newly_added: boolean }
	) {
		//this.props.updateFunc(event);
		this.state.options[index] = option_value;

		this.updateParent(this.state.options, this.state.poll_type);
	}

	updateParent(new_raw_options: PollOption[], poll_type: PollType) {
		let cleaned_options = getListWithoutNulls(new_raw_options);

		this.setState({
			options: new_raw_options,
			cleaned_options: cleaned_options,
		});

		this.props.updateFunc(cleaned_options, poll_type);
	}

	createPollAddonProps(type: PollType): AddonButtonProps {
		const label = type === PollType.Standard ? "Generic" : "Time";

		return {
			onClick: (e) => {
				e.preventDefault();

				const new_poll_options: PollOption[] = [
					createInitialPollOption(type),
					createInitialPollOption(type),
				];

				this.setState({
					poll_type: type,
					cleaned_options: new_poll_options,
					options: new_poll_options,
				});

				this.updateParent(new_poll_options, type);
			},
			icon: undefined,
			label,
			selected: this.state.poll_type === type,
			disabled: this.props.is_edit || this.props.disabled,
		};
	}

	render() {
		let result = [];

		let not_editable_options = this.state.cleaned_options.filter(
			(val) => !val.is_newly_added
		);

		let deletable_index = not_editable_options.length - 1;

		this.state.options.forEach((value, index) => {
			if (value == null) {
				return;
			}

			let deletable = value.is_newly_added || deletable_index === index;

			result.push(this.PollOption(index, deletable));
		});
		result.push(this.AddOptionButton(result.length));

		let name =
			this.props.name +
			" " +
			`(${this.state.cleaned_options.length} / ${Constants.MAX_POLL_OPTIONS})`;

		const addon_button_data: AddonButtonProps[] = [];

		addon_button_data.push(this.createPollAddonProps(PollType.Standard));
		addon_button_data.push(this.createPollAddonProps(PollType.Time));

		return (
			<div className="field">
				{this.props.name &&
					DashboardUtils.CreateHeaderAndInfoButton(
						this.props.name,
						this.props.info
					)}
				<div className="control">
					<div className="columns is-multiline">
						<div className="column is-full">
							<DashboardUtils.AddOnButtonGroup
								addon_button_data={addon_button_data}
							/>
						</div>
						{this.state.poll_type === PollType.Time && (
							<div className="column is-full">
								Enter poll options using dates and times in your
								local timezone.
							</div>
						)}
						{this.state.poll_type === PollType.Standard && (
							<div className="column is-full">
								Enter poll options using simple text.
							</div>
						)}
						<div className="column is-full">
							<ul className="!mx-0">{result}</ul>
						</div>
					</div>
				</div>
			</div>
		);
	}

	onAddButtonPressed(event) {
		event.preventDefault();

		let new_option: PollOption;
		if (this.state.poll_type === PollType.Standard) {
			new_option = createInitialPollOption(this.state.poll_type);
		} else {
			const option_to_clone =
				this.state.options[this.state.options.length - 1];

			if (option_to_clone) {
				new_option = {
					is_newly_added: true,
					name: option_to_clone.name,
				};
			} else {
				new_option = createInitialPollOption(this.state.poll_type);
			}
		}

		this.state.options.push(new_option);
		this.updateParent(this.state.options, this.state.poll_type);
	}

	AddOptionButton(index) {
		let className = "";

		let button_text = "Add Option";
		let disabled =
			this.state.cleaned_options.length >= Constants.MAX_POLL_OPTIONS ||
			this.props.disabled;

		let button = (
			<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
				<button
					disabled={disabled}
					onClick={this.onAddButtonPressed}
					className="dashboard-primary-button--tiny"
				>
					{button_text}
				</button>
			</FadeIn>
		);

		return (
			<div key="add" className="columns is-gapless is-mobile !mx-0">
				<div className="column is-11 !mx-0">
					<div className="columns is-mobile is-centered">
						<div className="column is-narrow">
							{!disabled && button}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
